import { NextPage } from 'next'
import { Container } from '@ucheba/ui/components/Container/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { specialitiesSearchSelectors } from '@ucheba/store/specialities/search'
import { Text } from '@ucheba/ui/components/Text/desktop'
import Link from 'next/link'
import Image from 'next/image'
import { Aside } from '@ucheba/ui/components/Aside/desktop'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { useRouter } from 'next/router'
import { analyticEvent } from '@ucheba/utils/helpers/analytics/events'
import {
  EAnalyticEvents,
  EAnalyticEventTargets,
  EAnalyticSections,
} from '@ucheba/utils/helpers/analytics/events/types'
import { getSingleQuery } from '@ucheba/utils/helpers/router'
import { Banner } from '../../../../../components/Banner/desktop'
import { ISpecialitiesSearchPageProps } from '../types'
import { SpecialitiesList } from '../../../../../components/SpecialitiesList/desktop'
import SpecialitiesSearchPageFacets from './SpecialitiesSearchPageFacets'
import SpecialitiesSearchPageSelectedFacets from './SpecialitiesSearchPageSelectedFacets'
import { SpecialitiesPageHeader } from '../../index/desktop'
import { specialitiesPath } from '../../index/constants'
import data from '../data.json'
import SpecialitiesSearchPagePagination from './SpecialitiesSearchPagePagination'
import { useSpecialitiesSearchPage, useSpecialitiesSearchPageFacetsCore } from '../bll'
import emptySearchImage from '../assets/img__empty-search.png'
import { forAbiturients } from '../../../../../routs'
import LidMagnetForm from '../../../../../components/LidMagnetForm/desktop'
import {
  EApplicationFormProductTypes,
  EApplicationFunnels,
} from '../../../../../components/AppliactionForm/types'
import { ETypeRequest } from '../../../ege/index/types'

/** Страница поиска специальностей */
const SpecialitiesSearchPage: NextPage<ISpecialitiesSearchPageProps> = () => {
  const searchResults = useSelector(specialitiesSearchSelectors.selectAll)
  const { emptySearch } = data
  const { query, asPath } = useRouter()
  const { title } = useSpecialitiesSearchPage()
  const { facets, onSubmit, initialValues, isFriendlyUrlPage, facetsState } =
    useSpecialitiesSearchPageFacetsCore()

  const topProgramTypesQuery = useMemo(() => {
    if (query['topProgramTypes[]']) {
      return query['topProgramTypes[]']
    }

    if (query.topProgramTypes) return query.topProgramTypes

    return null
  }, [query])

  const params = useMemo(() => {
    if (topProgramTypesQuery) {
      return {
        ...query,
        programTypes: topProgramTypesQuery,
      }
    }

    return {
      ...query,
      'programTypes[]': query?.params?.includes('vocational')
        ? [EProgramTypes.vocational, 11, 12]
        : [EProgramTypes.higher, 1, 2],
    }
  }, [query, topProgramTypesQuery])

  const LeadFormComponent = useMemo(() => {
    const topProgramTypes = getSingleQuery(topProgramTypesQuery)

    if (topProgramTypes === '1001' || asPath.includes('vocational')) {
      return (
        <LidMagnetForm
          title='Хочешь сдать ОГЭ на&nbsp;5+ и&nbsp;поступить в&nbsp;колледж на&nbsp;бюджет?'
          text='Поможем подготовиться к&nbsp;ОГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
          buttonText='Подготовиться к ОГЭ'
          size='small'
          funnel={EApplicationFunnels.speciality_rubrics}
          productType={EApplicationFormProductTypes.oge}
          requestTarget={ETypeRequest.oge_lead_request}
          onSubmitForm={() => {
            analyticEvent({
              targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
              eventName: EAnalyticEvents.leadFormRequestSpeciality,
              section: EAnalyticSections.leadMagnetForm,
            })
          }}
          list={[
            'Расскажем о системе эффективной подготовки',
            'Познакомим с онлайн-школой и платформой',
            'Ответим на вопросы и запишем на занятия',
          ]}
        />
      )
    }

    return (
      <LidMagnetForm
        title='Хочешь сдать ЕГЭ на&nbsp;80+ и&nbsp;поступить в&nbsp;вуз на&nbsp;бюджет?'
        text='Поможем подготовиться к&nbsp;ЕГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
        buttonText='Подготовиться к ЕГЭ'
        size='small'
        funnel={EApplicationFunnels.speciality_rubrics}
        productType={EApplicationFormProductTypes.ege}
        requestTarget={ETypeRequest.ege_lead_request}
        onSubmitForm={() => {
          analyticEvent({
            targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
            eventName: EAnalyticEvents.leadFormRequestSpeciality,
            section: EAnalyticSections.leadMagnetForm,
          })
        }}
        list={[
          'Расскажем о системе эффективной подготовки',
          'Познакомим с онлайн-школой и платформой',
          'Ответим на вопросы и запишем на занятия',
        ]}
      />
    )
  }, [asPath, topProgramTypesQuery])

  const items = useMemo(() => {
    if (searchResults) {
      return searchResults.map((item, key, arr) => {
        if (arr.length < 3 && key === arr.length - 1) {
          return { ...item, element: <Banner id={2} extraParams={params} /> }
        }

        if (key === 2 || key === 9) {
          return { ...item, element: <Banner id={2} extraParams={params} /> }
        }

        if (key === 5) {
          return { ...item, element: LeadFormComponent }
        }

        return item
      })
    }

    return []
  }, [LeadFormComponent, params, searchResults])

  return (
    <Container>
      <SpecialitiesPageHeader
        title={title}
        href={forAbiturients.speciality.search.path}
      />

      <Spacing spacing='large' />

      <Grid>
        <GridCol uxs={18} as='article'>
          <SpecialitiesSearchPageSelectedFacets facetsState={facetsState} />

          {items.length ? (
            <>
              <SpecialitiesList items={items} addSearchQueryToInstitutionHref />

              <SpecialitiesSearchPagePagination
                facetsState={facetsState}
                isFriendlyUrlPage={isFriendlyUrlPage}
              />
            </>
          ) : (
            <>
              <Spacing spacing='uxlarge' />

              <Heading h3={emptySearch.title} textAlign='center' />

              <Text textAlign='center' as='p'>
                {emptySearch.hint}
              </Text>

              <Text textAlign='center' html={false}>
                {emptySearch.catalogText}

                <Link href={specialitiesPath} passHref>
                  {emptySearch.catalogTextLink}
                </Link>
              </Text>

              <Spacing spacing='large' />

              <Grid justifyContent='center'>
                <GridCol width='auto'>
                  <Image src={emptySearchImage} width={200} height={168} />
                </GridCol>
              </Grid>

              <Spacing spacing='uxlarge' />
            </>
          )}
        </GridCol>

        <GridCol uxs={6} as='aside'>
          <Aside>
            <SpecialitiesSearchPageFacets
              facets={facets}
              isFriendlyUrlPage={isFriendlyUrlPage}
              initialValues={initialValues}
              onSubmit={onSubmit}
            />
          </Aside>
        </GridCol>
      </Grid>

      <Spacing spacing='ularge' />

      <Banner id={6} extraParams={params} />

      <Banner id={14} extraParams={params} />
    </Container>
  )
}

export default SpecialitiesSearchPage
